import type {
    MarketingGranularity,
    MarketingSeriesPoint,
} from '@/lib/api/marketing/marketing-dashboard-api'
import { useContainerWidth } from '@/lib/hooks/use-container-width'
import { useState } from 'react'
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts'
import DeltaBadge from './delta-badge'
import {
    deltaOf,
    formatBucket,
    formatBucketLong,
    formatCurrency,
    formatNumber,
} from './marketing-format'

const CHART_HEIGHT = 280
const CURRENT_COLOR = '#2a78d6'
const PREVIOUS_COLOR = '#9ca3af'

type MetricKey = 'leads' | 'paidLeads' | 'salesValue' | 'newChats' | 'spend'

const METRICS: Array<{ key: MetricKey; label: string; currency?: boolean }> = [
    { key: 'leads', label: 'Leads' },
    { key: 'paidLeads', label: 'Leads de anúncio' },
    { key: 'newChats', label: 'Conversas novas' },
    { key: 'salesValue', label: 'Vendas (R$)', currency: true },
    { key: 'spend', label: 'Investimento (R$)', currency: true },
]

interface MarketingTrendChartProps {
    current: MarketingSeriesPoint[]
    previous: MarketingSeriesPoint[] | null
    granularity: MarketingGranularity
    /** Leads de anúncio e investimento só fazem sentido fora do recorte orgânico. */
    showAdMetrics?: boolean
}

/**
 * Uma métrica por vez (troca no seletor), atual contra o período comparado ponto a
 * ponto. Uma escala só: leads e reais no mesmo gráfico pediriam dois eixos, e gráfico
 * de dois eixos engana. O comparado é tracejado e cinza — a diferença não depende só
 * da cor.
 */
export default function MarketingTrendChart({
    current,
    previous,
    granularity,
    showAdMetrics = true,
}: MarketingTrendChartProps) {
    const { ref, width } = useContainerWidth()
    const metrics = showAdMetrics ? METRICS : METRICS.filter(m => m.key !== 'spend' && m.key !== 'paidLeads')
    const [chosen, setMetric] = useState<MetricKey>('leads')
    const metric = metrics.some(m => m.key === chosen) ? chosen : 'leads'
    const meta = metrics.find(m => m.key === metric)!

    const data = current.map((point, index) => {
        const prev = previous?.[index]
        return {
            label: formatBucket(point.key, granularity),
            currentKey: point.key,
            previousKey: prev?.key ?? null,
            current: meta.currency ? point[metric] / 100 : point[metric],
            previous: prev ? (meta.currency ? prev[metric] / 100 : prev[metric]) : null,
        }
    })

    const totalCurrent = current.reduce((acc, p) => acc + p[metric], 0)
    const totalPrevious = previous ? previous.reduce((acc, p) => acc + p[metric], 0) : null
    const formatTotal = (v: number) => (meta.currency ? formatCurrency(v) : formatNumber(v))
    const formatAxis = (v: number) =>
        new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(v)
    const formatPoint = (v: number) =>
        meta.currency
            ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)
            : formatNumber(v)

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5 h-full">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                    <h3 className="text-lg font-semibold text-base-content">Evolução no período</h3>
                    <p className="text-sm text-base-content/60">
                        {previous ? 'Linha cheia: período atual. Tracejada: período comparado.' : 'Período atual.'}
                    </p>
                </div>
                <div className="join">
                    {metrics.map(m => (
                        <button
                            key={m.key}
                            type="button"
                            className={`btn btn-xs join-item ${metric === m.key ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                            onClick={() => setMetric(m.key)}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mb-2 flex flex-wrap items-center gap-4 text-xs text-base-content/70">
                <span className="flex items-center gap-1.5">
                    <span className="inline-block h-0.5 w-4 rounded" style={{ backgroundColor: CURRENT_COLOR }} />
                    Atual: <strong className="text-base-content">{formatTotal(totalCurrent)}</strong>
                </span>
                {totalPrevious != null && (
                    <span className="flex items-center gap-1.5">
                        <span
                            className="inline-block h-0 w-4 border-t-2 border-dashed"
                            style={{ borderColor: PREVIOUS_COLOR }}
                        />
                        Comparado: <strong className="text-base-content">{formatTotal(totalPrevious)}</strong>
                        <DeltaBadge delta={deltaOf(totalCurrent, totalPrevious)} />
                    </span>
                )}
            </div>

            <div ref={ref} className="w-full" style={{ height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}>
                {width > 0 && (
                    <LineChart
                        data={data}
                        width={width}
                        height={CHART_HEIGHT}
                        margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} className="opacity-30" />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                            minTickGap={16}
                        />
                        <YAxis
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                            width={56}
                            allowDecimals={!!meta.currency}
                            tickFormatter={v => formatAxis(Number(v) || 0)}
                        />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null
                                const row = payload[0].payload as (typeof data)[number]
                                return (
                                    <div className="rounded-lg border border-base-300 bg-base-100 px-3 py-2 text-xs shadow-md">
                                        <p className="font-semibold mb-1">{meta.label}</p>
                                        <p className="flex items-center gap-2">
                                            <span className="inline-block h-0.5 w-3" style={{ backgroundColor: CURRENT_COLOR }} />
                                            {formatBucketLong(row.currentKey, granularity)}:{' '}
                                            <strong>{formatPoint(row.current)}</strong>
                                        </p>
                                        {row.previousKey && row.previous != null && (
                                            <p className="flex items-center gap-2 text-base-content/70">
                                                <span
                                                    className="inline-block h-0 w-3 border-t-2 border-dashed"
                                                    style={{ borderColor: PREVIOUS_COLOR }}
                                                />
                                                {formatBucketLong(row.previousKey, granularity)}:{' '}
                                                <strong>{formatPoint(row.previous)}</strong>
                                            </p>
                                        )}
                                    </div>
                                )
                            }}
                        />
                        {previous && (
                            <Line
                                type="linear"
                                dataKey="previous"
                                stroke={PREVIOUS_COLOR}
                                strokeWidth={2}
                                strokeDasharray="5 4"
                                dot={false}
                                activeDot={{ r: 4 }}
                                isAnimationActive={false}
                            />
                        )}
                        <Line
                            type="linear"
                            dataKey="current"
                            stroke={CURRENT_COLOR}
                            strokeWidth={2}
                            dot={data.length <= 12 ? { r: 4, strokeWidth: 2, fill: 'oklch(var(--b1))' } : false}
                            activeDot={{ r: 5 }}
                            isAnimationActive={false}
                        />
                    </LineChart>
                )}
            </div>
        </div>
    )
}
